import React from "react";
import { UpdateRoleMutationVariables } from "../../../types/graphql";
import ChoosePermissionItem from "../ChoosePermissionItem";
import RadioBtnsComponent from "../RadioBtnsComponent";
import { useTranslation } from "react-i18next";

import "./styles.css";

interface IPermissions {
    fields: UpdateRoleMutationVariables;
    setFields: React.Dispatch<React.SetStateAction<UpdateRoleMutationVariables>>;
}

const ManageRolePermissions: React.FC<IPermissions> = ({ fields, setFields }) => {
    const { t } = useTranslation("admin");
    const changePermission = (nameOfPermission: string, value: boolean) => {
        setFields((prevState) => {
            return {
                ...prevState,
                permissions: {
                    ...prevState?.permissions,
                    [nameOfPermission]: !value,
                },
            };
        });
    };

    return (
        <div className="manage-role-permissions">
            <div className="manage-role_permission-container">
                <ChoosePermissionItem
                    name={`1. ${t("permissions.usersPageAccess")}`}
                    nameOfPermission={"accessUserPage"}
                    value={fields?.permissions?.accessUserPage || false}
                    changePermission={changePermission}
                />
                <div className="manage-role_sub-group">
                    <div className="manage-role__subtitle">{t("permissions.access")}</div>
                    <RadioBtnsComponent
                        changeableField="accessAllGroups"
                        fields={fields}
                        setFields={setFields}
                        radio1Title={t("permissions.allGroups")}
                        radio2Title={t("permissions.roleGroups")}
                        disabled={!fields.permissions.accessUserPage}
                    />
                    <ChoosePermissionItem
                        name={`1.1 ${t("permissions.createGroups")}`}
                        nameOfPermission={"createGroup"}
                        value={fields?.permissions?.createGroup || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.accessUserPage}
                    />
                    <ChoosePermissionItem
                        name={`1.2 ${t("permissions.registerUsers")}`}
                        nameOfPermission={"registerUser"}
                        value={fields?.permissions?.registerUser || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.accessUserPage}
                    />
                    <ChoosePermissionItem
                        name={`1.3 ${t("permissions.seeUserData")}`}
                        nameOfPermission={"seeUserData"}
                        value={fields?.permissions?.seeUserData || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.accessUserPage}
                    />
                    <div className="manage-role_sub-group">
                        <ChoosePermissionItem
                            name={`1.3.1 ${t("permissions.editUserData")}`}
                            nameOfPermission={"editUserData"}
                            value={fields?.permissions?.editUserData || false}
                            changePermission={changePermission}
                            disabled={!fields.permissions.seeUserData}
                        />
                        <ChoosePermissionItem
                            name={`1.3.2 ${t("permissions.editPayments")}`}
                            nameOfPermission={"editPayments"}
                            value={fields?.permissions?.editPayments || false}
                            changePermission={changePermission}
                            disabled={!fields.permissions.seeUserData}
                        />
                        <div className="manage-role_sub-group">
                            <ChoosePermissionItem
                                name={t("permissions.editBalance")}
                                nameOfPermission={"editBalance"}
                                value={fields?.permissions?.editBalance || false}
                                changePermission={changePermission}
                                disabled={!fields.permissions.editPayments}
                            />
                            <ChoosePermissionItem
                                name={t("permissions.editFreeDuels")}
                                nameOfPermission={"giveFreeDuels"}
                                value={fields?.permissions?.giveFreeDuels || false}
                                changePermission={changePermission}
                                disabled={!fields.permissions.editPayments}
                            />
                            <ChoosePermissionItem
                                name={t("permissions.editMembershipDates")}
                                nameOfPermission={"editDateMembership"}
                                value={fields?.permissions?.editDateMembership || false}
                                changePermission={changePermission}
                                disabled={!fields.permissions.editPayments}
                            />
                            <ChoosePermissionItem
                                name={t("permissions.editTrainingDates")}
                                nameOfPermission={"editDateTraining"}
                                value={fields?.permissions?.editDateTraining || false}
                                changePermission={changePermission}
                                disabled={!fields.permissions.editPayments}
                            />
                        </div>
                    </div>
                    <ChoosePermissionItem
                        name={`1.4 ${t("permissions.seeTransactions")}`}
                        nameOfPermission={"seeTransactions"}
                        value={fields?.permissions?.seeTransactions || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.accessUserPage}
                    />
                    <ChoosePermissionItem
                        name={`1.5 ${t("permissions.setParticipationTime")}`}
                        nameOfPermission={"setDuelTime"}
                        value={fields?.permissions?.setDuelTime || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.accessUserPage}
                    />
                    <ChoosePermissionItem
                        name={`1.6 ${t("permissions.setDuelType")}`}
                        nameOfPermission={"setDuelType"}
                        value={fields?.permissions?.setDuelType || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.accessUserPage}
                    />
                    <ChoosePermissionItem
                        name={`1.7 ${t("permissions.setTasks")}`}
                        nameOfPermission={"setTasks"}
                        value={fields?.permissions?.setTasks || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.accessUserPage}
                    />
                    <ChoosePermissionItem
                        name={`1.8 ${t("permissions.setAdmins")}`}
                        nameOfPermission={"setAdmin"}
                        value={fields?.permissions?.setAdmin || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.accessUserPage}
                    />
                    <ChoosePermissionItem
                        name={`1.9 ${t("permissions.organizeDuels")}`}
                        nameOfPermission={"organizeDuel"}
                        value={fields?.permissions?.organizeDuel || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.accessUserPage}
                    />
                    <div className="manage-role_sub-group">
                        <ChoosePermissionItem
                            name={`1.9.1 ${t("permissions.broadcastYoutube")}`}
                            nameOfPermission={"broadcastYoutube"}
                            value={fields?.permissions?.broadcastYoutube || false}
                            changePermission={changePermission}
                            disabled={!fields.permissions.organizeDuel}
                        />
                    </div>
                </div>
            </div>
            <div className="manage-role_permission-container">
                <ChoosePermissionItem
                    name={`2. ${t("permissions.createTasks")}`}
                    nameOfPermission={"createTask"}
                    value={fields?.permissions?.createTask || false}
                    changePermission={changePermission}
                />
            </div>
            <div className="manage-role_permission-container">
                <ChoosePermissionItem
                    name={`3. ${t("permissions.freeParticipation")}`}
                    nameOfPermission={"participateFree"}
                    value={fields?.permissions?.participateFree || false}
                    changePermission={changePermission}
                />
                <div className="manage-role_sub-group group-checkboxes">
                    <ChoosePermissionItem
                        name={t("permissions.freeJudge")}
                        nameOfPermission={"participateFreeJudge"}
                        value={fields?.permissions?.participateFreeJudge || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.participateFree}
                    />
                    <ChoosePermissionItem
                        name={t("permissions.freeMaster")}
                        nameOfPermission={"participateFreeMaster"}
                        value={fields?.permissions?.participateFreeMaster || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.participateFree}
                    />
                    <ChoosePermissionItem
                        name={t("permissions.freePlayer")}
                        nameOfPermission={"participateFreePlayer"}
                        value={fields?.permissions?.participateFreePlayer || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.participateFree}
                    />
                </div>
            </div>
            <div className="manage-role_permission-container">
                <ChoosePermissionItem
                    name={`4. ${t("permissions.seeVideos")}`}
                    nameOfPermission={"seeVideo"}
                    value={fields?.permissions?.seeVideo || false}
                    changePermission={changePermission}
                />
                <div className="manage-role_sub-group">
                    <RadioBtnsComponent
                        changeableField="seeVideoAllGroups"
                        fields={fields}
                        setFields={setFields}
                        radio1Title={t("permissions.seeVideosAllGroups")}
                        radio2Title={t("permissions.seeVideosOwnGroups")}
                        disabled={!fields.permissions.seeVideo}
                    />
                    <div className="group-checkboxes">
                        <ChoosePermissionItem
                            name={t("permissions.seeVideosCompleted")}
                            nameOfPermission={"seeVideoCompleted"}
                            value={fields?.permissions?.seeVideoCompleted || false}
                            changePermission={changePermission}
                            disabled={!fields.permissions.seeVideo}
                        />
                        <ChoosePermissionItem
                            name={t("permissions.seeVideosNotCompleted")}
                            nameOfPermission={"seeVideoNotCompleted"}
                            value={fields?.permissions?.seeVideoNotCompleted || false}
                            changePermission={changePermission}
                            disabled={!fields.permissions.seeVideo}
                        />
                    </div>
                </div>
            </div>
            <div className="manage-role_permission-container">
                <ChoosePermissionItem
                    name={`5. ${t("permissions.adminChat")}`}
                    nameOfPermission={"adminChat"}
                    value={fields?.permissions?.adminChat || false}
                    changePermission={changePermission}
                />
            </div>
            <div className="manage-role_permission-container">
                <ChoosePermissionItem
                    name={`6. ${t("permissions.instructionsRights")}`}
                    nameOfPermission={"createInstruction"}
                    value={fields?.permissions?.createInstruction || false}
                    changePermission={changePermission}
                    disabled={false}
                />
                <div className="manage-role_sub-group">
                    <ChoosePermissionItem
                        name={`6.1. ${t("permissions.createInstructions")}`}
                        nameOfPermission={"createInstruction"}
                        value={fields?.permissions?.createInstruction || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.createInstruction}
                    />
                    <ChoosePermissionItem
                        name={`6.2. ${t("permissions.assignInstructions")}`}
                        nameOfPermission={"setInstruction"}
                        value={fields?.permissions?.setInstruction || false}
                        changePermission={changePermission}
                        disabled={!fields.permissions.createInstruction}
                    />
                </div>
            </div>
            <div className="manage-role_permission-container">
                <ChoosePermissionItem
                    name={`7. ${t("permissions.bcAdmin")}`}
                    nameOfPermission={"bcAdmin"}
                    value={fields?.permissions?.bcAdmin || false}
                    changePermission={changePermission}
                />
            </div>
            <div className="manage-role_permission-container">
                <ChoosePermissionItem
                    name={`8. ${t("permissions.seeEventStats")}`}
                    nameOfPermission={"seeEventStatistic"}
                    value={fields?.permissions?.seeEventStatistic || false}
                    changePermission={changePermission}
                />
            </div>

            <div className="manage-role_permission-container">
                <ChoosePermissionItem
                    name={`9. ${t("permissions.setDuelName")}`}
                    nameOfPermission={"setDuelName"}
                    value={fields?.permissions?.setDuelName || false}
                    changePermission={changePermission}
                />
            </div>
        </div>
    );
};

export default ManageRolePermissions;
